import {
  Badge,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Textarea
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconRefresh,
  IconDeviceFloppy,
  IconPencil,
  IconX,
  IconCrown
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api.js";

interface CustomerDetail {
  workspaceId: string;
  name: string;
  slug: string;
  legalName: string | null;
  billingEmail: string | null;
  planKey: string | null;
  status: string | null;
  crmStage: string | null;
  notes: string | null;
  ownerEmail: string | null;
}

interface ImplementationProject {
  id: string;
  status: string;
  goLiveTargetAt: string | null;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    assignee: string | null;
    dueAt: string | null;
  }>;
}

interface BillingState {
  stripeCustomerId: string | null;
  planKey: string;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
}

interface AccessGrant {
  id: string;
  role: string;
  reason: string;
  expiresAt: string;
  internalUserEmail: string;
}

const PLAN_OPTIONS = [
  { value: "free", label: "Free — $0" },
  { value: "starter", label: "Starter — $49/mo" },
  { value: "pro", label: "Pro — $299/mo" },
  { value: "enterprise", label: "Enterprise — invoice" }
];

const BILLING_MODE_OPTIONS = [
  { value: "none", label: "None (free / internal)" },
  { value: "stripe_subscription", label: "Stripe subscription" },
  { value: "invoice", label: "Invoice (manual)" }
];

/** Structured override fields exposed in the ops UI. Anything not listed here can still
 *  be set via the assign-enterprise endpoint with a custom `overrides` payload. */
const NUMERIC_OVERRIDE_FIELDS: Array<{ key: string; label: string; description?: string }> = [
  { key: "included_production_controlled_actions_per_month", label: "Included production CA / mo" },
  { key: "included_sandbox_evaluations_per_month", label: "Included sandbox / mo" },
  { key: "max_members", label: "Max members" },
  { key: "max_extra_paid_members", label: "Max extra paid members" },
  { key: "max_workspaces", label: "Max workspaces" },
  { key: "max_extra_paid_workspaces", label: "Max extra paid workspaces" },
  { key: "max_action_types", label: "Max action types" },
  { key: "max_active_production_policies", label: "Max active prod policies" },
  { key: "audit_retention_days", label: "Audit retention (days)" }
];

const FEATURE_OVERRIDE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "feature_approval_groups", label: "Approval groups" },
  { key: "feature_escalation_rules", label: "Escalation rules" },
  { key: "feature_audit_export", label: "Audit export" },
  { key: "feature_sso", label: "SSO" },
  { key: "feature_scim", label: "SCIM" },
  { key: "feature_advanced_rbac", label: "Advanced RBAC" },
  { key: "feature_audit_streaming", label: "Audit streaming" },
  { key: "feature_sla", label: "SLA" },
  { key: "feature_invoice_billing", label: "Invoice billing" },
  { key: "feature_policy_simulation", label: "Policy simulation" },
  { key: "feature_standard_webhooks", label: "Standard webhooks" },
  { key: "feature_connector_hubspot", label: "Connector: HubSpot" },
  { key: "feature_connector_intercom", label: "Connector: Intercom" },
  { key: "feature_connector_zendesk", label: "Connector: Zendesk" }
];

const STATUS_OPTIONS = [
  { value: "trial", label: "Trial" },
  { value: "active", label: "Active" },
  { value: "churned", label: "Churned" },
  { value: "suspended", label: "Suspended" }
];

const CRM_STAGE_OPTIONS = [
  { value: "prospect", label: "Prospect" },
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
  { value: "at_risk", label: "At risk" },
  { value: "churned", label: "Churned" }
];

// ─── Overview / edit panel ────────────────────────────────────────────────────

const OverviewTab = ({
  customer,
  workspaceId
}: {
  customer: CustomerDetail;
  workspaceId: string;
}) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const form = useForm({
    initialValues: {
      legalName: customer.legalName ?? "",
      billingEmail: customer.billingEmail ?? "",
      status: customer.status ?? "trial",
      crmStage: customer.crmStage ?? "",
      notes: customer.notes ?? ""
    }
  });

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      api.patch(`/internal/customers/${workspaceId}`, {
        legalName: values.legalName.trim() || null,
        billingEmail: values.billingEmail.trim() || null,
        status: values.status || undefined,
        crmStage: values.crmStage.trim() || null,
        notes: values.notes.trim() || null
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["internal", "customers", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["internal", "customers"] });
      notifications.show({ title: "Saved", message: "Customer updated.", color: "green" });
      setEditing(false);
    },
    onError: (err: Error) => {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  });

  if (editing) {
    return (
      <Paper withBorder p="md" radius="md">
        <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
          <Stack gap="sm">
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={600}>
                Edit details
              </Text>
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconX size={14} />}
                onClick={() => {
                  form.reset();
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </Group>
            <TextInput
              label="Legal name"
              placeholder="Acme Corp Ltd."
              {...form.getInputProps("legalName")}
            />
            <TextInput
              label="Billing email"
              placeholder="billing@acme.com"
              {...form.getInputProps("billingEmail")}
            />
            <Select label="Status" data={STATUS_OPTIONS} {...form.getInputProps("status")} />
            <Select
              label="CRM stage"
              data={CRM_STAGE_OPTIONS}
              clearable
              value={form.values.crmStage || null}
              onChange={(v) => form.setFieldValue("crmStage", v ?? "")}
            />
            <Textarea
              label="Notes"
              placeholder="Internal notes..."
              autosize
              minRows={2}
              {...form.getInputProps("notes")}
            />
            <Group justify="flex-end">
              <Button
                type="submit"
                loading={save.isPending}
                leftSection={<IconDeviceFloppy size={14} />}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={600}>
          Details
        </Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPencil size={14} />}
          onClick={() => setEditing(true)}
        >
          Edit
        </Button>
      </Group>
      <Stack gap="xs">
        {[
          { label: "Workspace slug", value: customer.slug },
          { label: "Owner (SSO)", value: customer.ownerEmail ?? "—" },
          { label: "Legal name", value: customer.legalName ?? "—" },
          { label: "Billing email", value: customer.billingEmail ?? "—" },
          { label: "Status", value: customer.status ?? "—" },
          { label: "CRM stage", value: customer.crmStage ?? "—" },
          { label: "Notes", value: customer.notes ?? "—" }
        ].map((row) => (
          <Group key={row.label} justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
              {row.label}
            </Text>
            <Text size="sm" ta="right" style={{ wordBreak: "break-all" }}>
              {row.value}
            </Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
};

// ─── Entitlements tab ─────────────────────────────────────────────────────────

interface EntitlementsResponse {
  organizationId: string;
  entitlements: Record<string, unknown>;
  overrides: Record<string, unknown>;
  billingMode: "none" | "stripe_subscription" | "invoice";
}

interface OverrideDraft {
  planKey: string;
  billingMode: "none" | "stripe_subscription" | "invoice";
  numeric: Record<string, number | "">;
  features: Record<string, "inherit" | "on" | "off">;
  propagateToStripe: boolean;
}

const buildInitialDraft = (
  current: EntitlementsResponse | undefined,
  customerPlanKey: string | null
): OverrideDraft => {
  const numeric: Record<string, number | ""> = {};
  for (const f of NUMERIC_OVERRIDE_FIELDS) {
    const v = current?.overrides[f.key];
    numeric[f.key] = typeof v === "number" ? v : "";
  }
  const features: Record<string, "inherit" | "on" | "off"> = {};
  for (const f of FEATURE_OVERRIDE_FIELDS) {
    const v = current?.overrides[f.key];
    features[f.key] = v === true ? "on" : v === false ? "off" : "inherit";
  }
  return {
    planKey:
      typeof current?.overrides.plan_key === "string"
        ? (current.overrides.plan_key as string)
        : (customerPlanKey ?? "free"),
    billingMode: current?.billingMode ?? "none",
    numeric,
    features,
    propagateToStripe: false
  };
};

const buildOverridesPayload = (draft: OverrideDraft): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const f of NUMERIC_OVERRIDE_FIELDS) {
    const v = draft.numeric[f.key];
    if (typeof v === "number" && Number.isFinite(v)) {
      out[f.key] = v;
    }
  }
  for (const f of FEATURE_OVERRIDE_FIELDS) {
    const v = draft.features[f.key];
    if (v === "on") out[f.key] = true;
    if (v === "off") out[f.key] = false;
  }
  return out;
};

const formatScalar = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (v === -1 || v === "unlimited") return "∞";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
};

const EntitlementsTab = ({
  workspaceId,
  customerPlanKey
}: {
  workspaceId: string;
  customerPlanKey: string | null;
}) => {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["internal", "customers", workspaceId, "entitlements"],
    queryFn: () => api.get<EntitlementsResponse>(`/internal/customers/${workspaceId}/entitlements`)
  });

  const [draft, setDraft] = useState<OverrideDraft>(() => buildInitialDraft(data, customerPlanKey));
  useEffect(() => {
    setDraft(buildInitialDraft(data, customerPlanKey));
  }, [data, customerPlanKey]);

  const save = useMutation({
    mutationFn: () => {
      const overrides = buildOverridesPayload(draft);
      return api.post<{
        stripe?: { synced: boolean; reason?: string; detail?: string };
      }>(`/internal/customers/${workspaceId}/entitlements`, {
        entitlements: overrides,
        planKey: draft.planKey,
        billingMode: draft.billingMode,
        propagateToStripe: draft.propagateToStripe
      });
    },
    onSuccess: (resp) => {
      void qc.invalidateQueries({
        queryKey: ["internal", "customers", workspaceId, "entitlements"]
      });
      void qc.invalidateQueries({ queryKey: ["internal", "customers", workspaceId] });
      const stripe = resp.stripe;
      if (draft.propagateToStripe && stripe !== undefined) {
        notifications.show({
          title: stripe.synced ? "Saved + Stripe synced" : "Saved; Stripe sync skipped",
          message: stripe.synced
            ? "Overrides applied and pushed to Stripe (subscription metadata + line-item quantities)."
            : [stripe.reason, stripe.detail].filter(Boolean).join(" — ") ||
              "Stripe push not attempted",
          color: stripe.synced ? "green" : "orange"
        });
      } else {
        notifications.show({ title: "Saved", message: "Overrides applied.", color: "green" });
      }
    },
    onError: (err: Error) => {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  });

  const assignEnterprise = useMutation({
    mutationFn: () => {
      if (!data) throw new Error("not_loaded");
      const overrides = buildOverridesPayload(draft);
      return api.post(`/internal/organizations/${data.organizationId}/billing/assign-enterprise`, {
        billingMode: draft.billingMode === "none" ? "invoice" : draft.billingMode,
        overrides
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["internal", "customers", workspaceId, "entitlements"]
      });
      void qc.invalidateQueries({ queryKey: ["internal", "customers", workspaceId] });
      notifications.show({
        title: "Enterprise assigned",
        message:
          "Plan set to enterprise; overrides re-applied. Stripe was NOT modified — wire a custom Stripe sub manually if needed.",
        color: "green"
      });
    },
    onError: (err: Error) => {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  });

  if (isLoading || data === undefined) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  const overrideKeys = Object.keys(data.overrides ?? {});

  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Plan & billing
          </Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Plan key"
                description="Drives base limits and which add-on Stripe Prices apply."
                data={PLAN_OPTIONS}
                value={draft.planKey}
                onChange={(v) => v && setDraft({ ...draft, planKey: v })}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Billing mode"
                description="invoice = manually billed; stripe_subscription = managed by Stripe; none = free / internal."
                data={BILLING_MODE_OPTIONS}
                value={draft.billingMode}
                onChange={(v) =>
                  v &&
                  setDraft({
                    ...draft,
                    billingMode: v as OverrideDraft["billingMode"]
                  })
                }
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Numeric overrides
            </Text>
            <Text size="xs" c="dimmed">
              Empty = inherit from {draft.planKey} template
            </Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {NUMERIC_OVERRIDE_FIELDS.map((f) => (
              <NumberInput
                key={f.key}
                label={f.label}
                value={draft.numeric[f.key]}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    numeric: {
                      ...draft.numeric,
                      [f.key]: typeof v === "number" ? v : ""
                    }
                  })
                }
                min={0}
                step={1}
                size="xs"
                allowDecimal={false}
                placeholder="inherit"
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Feature overrides
            </Text>
            <Text size="xs" c="dimmed">
              inherit = use plan template; on/off = explicit override
            </Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {FEATURE_OVERRIDE_FIELDS.map((f) => (
              <Select
                key={f.key}
                label={f.label}
                size="xs"
                value={draft.features[f.key]}
                data={[
                  { value: "inherit", label: "inherit" },
                  { value: "on", label: "force on" },
                  { value: "off", label: "force off" }
                ]}
                onChange={(v) =>
                  v &&
                  setDraft({
                    ...draft,
                    features: {
                      ...draft.features,
                      [f.key]: v as "inherit" | "on" | "off"
                    }
                  })
                }
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md" style={{ background: "#fafafa" }}>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Effective entitlements (read-only)
          </Text>
          <Text size="xs" c="dimmed">
            What the API will enforce after merging plan template + Stripe sync + overrides.
          </Text>
          <Divider my={4} />
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={4}>
            {[...NUMERIC_OVERRIDE_FIELDS, ...FEATURE_OVERRIDE_FIELDS].map((f) => (
              <Group key={f.key} justify="space-between" wrap="nowrap">
                <Text size="xs" c="dimmed" truncate>
                  {f.label}
                </Text>
                <Text size="xs" ff="monospace">
                  {formatScalar(data.entitlements[f.key])}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
          {overrideKeys.length > 0 && (
            <>
              <Divider my={4} />
              <Text size="xs" c="dimmed">
                Currently stored overrides:{" "}
                <Text span ff="monospace">
                  {overrideKeys.join(", ")}
                </Text>
              </Text>
            </>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Switch
            label="Also push to Stripe on save"
            description="Requires an active Stripe subscription and API STRIPE_SECRET_KEY. Mirrors scalar entitlements to subscription metadata and updates seat/workspace line-item quantities."
            checked={draft.propagateToStripe}
            onChange={(e) => setDraft({ ...draft, propagateToStripe: e.currentTarget.checked })}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              size="xs"
              onClick={() => setDraft(buildInitialDraft(data, customerPlanKey))}
            >
              Reset
            </Button>
            <Button
              size="xs"
              loading={save.isPending}
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={() => save.mutate()}
            >
              Save overrides & plan
            </Button>
            <Button
              size="xs"
              color="violet"
              variant="light"
              loading={assignEnterprise.isPending}
              leftSection={<IconCrown size={14} />}
              onClick={() => assignEnterprise.mutate()}
            >
              Assign enterprise
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const CustomerDetailPage = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const qc = useQueryClient();

  const {
    data: customer,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ["internal", "customers", workspaceId],
    queryFn: () => api.get<CustomerDetail>(`/internal/customers/${workspaceId}`)
  });

  const { data: impl } = useQuery({
    queryKey: ["internal", "customers", workspaceId, "implementation"],
    queryFn: () =>
      api.get<ImplementationProject>(`/internal/customers/${workspaceId}/implementation`),
    retry: false
  });

  const { data: billing } = useQuery({
    queryKey: ["internal", "customers", workspaceId, "billing"],
    queryFn: () => api.get<BillingState>(`/internal/customers/${workspaceId}/billing`)
  });

  const syncBilling = useMutation({
    mutationFn: () => api.post("/internal/billing/sync", { workspaceId }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["internal", "customers", workspaceId, "billing"]
      });
    }
  });

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  if (isError || !customer) {
    return (
      <Stack gap="md" p="xl">
        <Button
          component={Link as React.ElementType}
          to="/customers"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          size="xs"
          style={{ alignSelf: "flex-start" }}
        >
          Back to customers
        </Button>
        <Text c="red" size="sm">
          {error instanceof Error ? error.message : "Customer not found."}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group>
        <Button
          component={Link as React.ElementType}
          to="/customers"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          size="xs"
        >
          Customers
        </Button>
      </Group>

      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{customer.name}</Title>
          <Group gap="xs" mt={4}>
            <Badge size="sm" variant="outline">
              {customer.planKey ?? "free"}
            </Badge>
            <Badge size="sm" color={customer.status === "active" ? "teal" : "gray"}>
              {customer.status ?? "new"}
            </Badge>
            <Text size="xs" c="dimmed" ff="monospace">
              {customer.workspaceId}
            </Text>
          </Group>
        </div>
      </Group>

      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="implementation">Implementation</Tabs.Tab>
          <Tabs.Tab value="entitlements">Entitlements</Tabs.Tab>
          <Tabs.Tab value="billing">Billing</Tabs.Tab>
          <Tabs.Tab value="access">Access grants</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewTab customer={customer} workspaceId={workspaceId ?? ""} />
        </Tabs.Panel>

        <Tabs.Panel value="implementation" pt="md">
          {impl ? (
            <Stack gap="sm">
              <Group>
                <Badge color={impl.status === "complete" ? "teal" : "blue"}>{impl.status}</Badge>
                {impl.goLiveTargetAt && (
                  <Text size="xs" c="dimmed">
                    Target: {new Date(impl.goLiveTargetAt).toLocaleDateString()}
                  </Text>
                )}
              </Group>
              <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Task</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Assignee</Table.Th>
                      <Table.Th>Due</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {impl.tasks.map((t) => (
                      <Table.Tr key={t.id}>
                        <Table.Td>
                          <Text size="sm">{t.title}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={t.status === "done" ? "teal" : "gray"}>
                            {t.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {t.assignee ?? "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {t.dueAt ? new Date(t.dueAt).toLocaleDateString() : "—"}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          ) : (
            <Text c="dimmed" size="sm">
              No implementation project found.
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="entitlements" pt="md">
          <EntitlementsTab workspaceId={workspaceId ?? ""} customerPlanKey={customer.planKey} />
        </Tabs.Panel>

        <Tabs.Panel value="billing" pt="md">
          {billing ? (
            <Card withBorder radius="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Title order={5}>Stripe billing</Title>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconRefresh size={14} />}
                    loading={syncBilling.isPending}
                    onClick={() => syncBilling.mutate()}
                  >
                    Sync
                  </Button>
                </Group>
                {[
                  { label: "Stripe customer ID", value: billing.stripeCustomerId ?? "—" },
                  { label: "Plan", value: billing.planKey },
                  { label: "Subscription status", value: billing.subscriptionStatus ?? "—" },
                  {
                    label: "Current period end",
                    value: billing.currentPeriodEnd
                      ? new Date(billing.currentPeriodEnd).toLocaleDateString()
                      : "—"
                  }
                ].map((row) => (
                  <Group key={row.label} justify="space-between">
                    <Text size="sm" c="dimmed">
                      {row.label}
                    </Text>
                    <Text size="sm">{row.value}</Text>
                  </Group>
                ))}
              </Stack>
            </Card>
          ) : (
            <Text c="dimmed" size="sm">
              No billing account linked.
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="access" pt="md">
          <AccessGrantsPanel workspaceId={workspaceId ?? ""} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

const AccessGrantsPanel = ({ workspaceId }: { workspaceId: string }) => {
  const qc = useQueryClient();
  const [role, setRole] = useState("");
  const [reason, setReason] = useState("");

  const { data } = useQuery({
    queryKey: ["internal", "customers", workspaceId, "access-grants"],
    queryFn: () =>
      api.get<{ grants: AccessGrant[] }>(`/internal/customers/${workspaceId}/access-grants`)
  });

  const grant = useMutation({
    mutationFn: () =>
      api.post(`/internal/customers/${workspaceId}/access-grants`, {
        role,
        reason,
        ttlMinutes: 120
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["internal", "customers", workspaceId, "access-grants"]
      });
      setRole("");
      setReason("");
    }
  });

  const grants = data?.grants ?? [];

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Active and recent support access grants for this workspace.
      </Text>
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Expires</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grants.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="xs" c="dimmed" ta="center" p="sm">
                    No access grants
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              grants.map((g) => (
                <Table.Tr key={g.id}>
                  <Table.Td>
                    <Text size="xs">{g.internalUserEmail}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="outline">
                      {g.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {g.reason}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c={new Date(g.expiresAt) < new Date() ? "red" : "dimmed"}>
                      {new Date(g.expiresAt).toLocaleString()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            Grant temporary access
          </Text>
          <Group grow>
            <TextInput
              placeholder="Role (e.g. support_engineer)"
              value={role}
              onChange={(e) => setRole(e.currentTarget.value)}
              size="sm"
            />
            <TextInput
              placeholder="Reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              size="sm"
            />
          </Group>
          <Button
            size="xs"
            loading={grant.isPending}
            disabled={!role || !reason}
            onClick={() => grant.mutate()}
          >
            Grant 2h access
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
};
